import { Switch } from '@heidi/design-system/switch';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@heidi/design-system/tooltip';
import { TypographyCaption } from '@heidi/design-system/typography';
import { useIntl } from 'react-intl';
import { libraryCreateFilterMessages } from '../libraryCreateFilter.messages';

interface TeamCollectionSwitchProps {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled: boolean;
  disabledMessage: string;
  label: string;
  /** Helper text shown as a tooltip (e.g. "Visible to all X members"). */
  helperText?: string;
}

/** Props shared by wizard steps that render the team collection toggle. */
export interface TeamToggleStepProps {
  isTeamCollection: boolean;
  onTeamCollectionChange: (checked: boolean) => void;
  hasTeam: boolean;
  canCreateTeamCollection: boolean;
  teamName?: string;
}

/** Team collection toggle for wizard footers; no-ops when the user has no team. */
export function TeamToggleContent(props: TeamToggleStepProps) {
  const intl = useIntl();

  if (!props.hasTeam) return null;

  const fallbackTeamName = intl.formatMessage({
    defaultMessage: 'Team',
    id: 'Kz1xJ5WLhE',
    description:
      'Fallback team name used in create-collection wizard copy when the real team name is unavailable',
  });
  const resolvedTeamName = props.teamName ?? fallbackTeamName;
  const isDisabled = !props.canCreateTeamCollection;

  return (
    <TeamCollectionSwitch
      checked={props.isTeamCollection}
      onCheckedChange={props.onTeamCollectionChange}
      disabled={isDisabled}
      disabledMessage={intl.formatMessage(
        libraryCreateFilterMessages.collectionSharingNotEnabled
      )}
      label={intl.formatMessage({
        defaultMessage: 'Team Collection',
        id: 'Eh8uucaLNJ',
        description:
          'Label for the toggle that marks a new collection as a team collection',
      })}
      helperText={intl.formatMessage(
        {
          defaultMessage: 'Visible to all {teamName} members',
          id: 'hrWQrWaond',
          description:
            'Helper tooltip explaining that a team collection is visible to all members of the named team',
        },
        { teamName: resolvedTeamName }
      )}
    />
  );
}

/** Compact inline team toggle for the wizard footer. */
export const TeamCollectionSwitch = ({
  checked,
  onCheckedChange,
  disabled,
  disabledMessage,
  label,
  helperText,
}: TeamCollectionSwitchProps) => {
  const displayedChecked = disabled ? false : checked;
  const showTooltip = disabled || !!helperText;
  const tooltipMessage = disabled ? disabledMessage : helperText;

  const toggle = (
    <div className="flex items-center gap-2">
      <Switch
        size="sm"
        checked={displayedChecked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
      />
      <TypographyCaption className="text-text-secondary">{label}</TypographyCaption>
    </div>
  );

  if (showTooltip) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>{toggle}</TooltipTrigger>
          <TooltipContent>{tooltipMessage}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return toggle;
};
